import styled from 'styled-components';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, map } from 'lodash-es';
import { ContactInfo, ContactStatus } from '@dev/ee-api/ee-api-models';
import { convertToLocalTime } from '@dev/ee-tools';
import { CustomTableRowsProps, palette } from '@dev/ee-components';

import { getUnsubscribeContactsStore, SetPickedContactEmail } from '@/store/contacts/actions';
import { StatusBadge } from '@/assets/StatusBadge';

import { DateAndTimeProps, TableBadgeInterface } from '../../UnsubscribedInterface';

import i18n from '../../../../../../i18n';
const EmailContainer = styled.div`
    word-break: break-all;
    padding-left: 5px;
`;

const DateAndTimeWrapper = styled.div`
    word-break: break-all;
`;

const TableTextWrapper = styled.div`
    color: ${palette.gray[7]};
    font: normal normal normal 14px/20px Nunito;
`;

const TableDateWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    color: ${palette.gray[7]};
    font: normal normal normal 14px/20px Nunito;
`;

const TextFirstLastName = styled.div`
    color: ${palette.gray[5]};
    font: normal normal normal 11px/14px Mulish;
`;

const TextSecondLine = styled.div`
    display: flex;
    justify-content: flex-end;
    color: ${palette.gray[5]};
    font: normal normal normal 11px/14px Mulish;
`;

const ContactEmailWrapper = styled.span`
    &:hover {
        text-decoration: underline;
        cursor: pointer;
    }
`;

const contactFirstAndLastName = (firstName: string, lastName: string): string =>
    `${!firstName ? '' : firstName} ${!lastName ? '' : lastName}`;

const ContactDateAndTime: FC<DateAndTimeProps> = (props: DateAndTimeProps): JSX.Element => {
    const { dateTime } = props;

    if (isEmpty(dateTime)) {
        return null;
    }

    return (
        <DateAndTimeWrapper>
            <TableDateWrapper>{convertToLocalTime(dateTime, 'L')}</TableDateWrapper>
            <TextSecondLine>{convertToLocalTime(dateTime, 'LT')}</TextSecondLine>
        </DateAndTimeWrapper>
    );
};

const TableBadge: FC<TableBadgeInterface> = (props: TableBadgeInterface): JSX.Element => {
    const { t } = useTranslation();

    const { status } = props;

    if (isEmpty(status)) {
        return null;
    }

    let value: string;

    switch (status) {
        case ContactStatus.Unsubscribed:
            value = t('unsubscribed');
            break;
        case ContactStatus.Abuse:
            value = t('complained');
            break;
        case ContactStatus.Transactional:
            value = t('transactional');
            break;
        case ContactStatus.Engaged:
            value = t('engaged');
            break;
        case ContactStatus.Active:
            value = t('active');
            break;
        case ContactStatus.Bounced:
            value = t('bounced');
            break;
        case ContactStatus.NotConfirmed:
            value = t('notconfirmedspace');
            break;
        case ContactStatus.Stale:
            value = t('stale');
            break;
        case ContactStatus.Inactive:
            value = t('inactive');
            break;
        default:
            value = '';
            break;
    }

    return <StatusBadge value={value} status={status} />;
};

export const reasonsTranslatedShort = {
    Unknown: i18n.t('unknown_short'),
    NoLongerWant: i18n.t('nolongerwant_short'),
    IrrelevantContent: i18n.t('irrelevantcontent_short'),
    TooFrequent: i18n.t('toofrequent_short'),
    NeverConsented: i18n.t('neverconsented_short'),
    DeceptiveContent: i18n.t('deceptivecontent_short'),
};

export const reasonsTranslated = {
    Unknown: i18n.t('unknown'),
    NoLongerWant: i18n.t('nolongerwant'),
    IrrelevantContent: i18n.t('irrelevantcontent'),
    TooFrequent: i18n.t('toofrequent'),
    NeverConsented: i18n.t('neverconsented'),
    DeceptiveContent: i18n.t('deceptivecontent'),
};

export const methodsTranslated = {
    UnsubscribePage: i18n.t('unsubscribepage'),
    UnsubscribeHeader: i18n.t('unsubscribeheader'),
    ThirdPartyLink: i18n.t('thirdpartylink'),
    ManualRemoval: i18n.t('manualremoval'),
    SpamReport: i18n.t('spamreport'),
};

export const useTableRowData = (): CustomTableRowsProps[] => {
    const { t } = useTranslation();

    const { contactsList } = useSelector(getUnsubscribeContactsStore);
    const dispatch = useDispatch();

    if (isEmpty(contactsList)) {
        return [];
    }

    return map(contactsList, (contact: ContactInfo, index: number) => {
        const methodTranslation = contact.Method ? t(`${contact.Method.toLowerCase()}`) : null;
        const reasonTranslation = contact.Reason ? t(`${contact.Reason.toLowerCase()}_short`) : null;
        return {
            email: (
                <EmailContainer>
                    <TableTextWrapper>
                        <ContactEmailWrapper
                            onClick={() => {
                                dispatch(SetPickedContactEmail(contact.Email));
                            }}
                        >
                            {contact.Email}
                        </ContactEmailWrapper>
                    </TableTextWrapper>
                    <TextFirstLastName>
                        {contactFirstAndLastName(contact.FirstName, contact.LastName)}
                    </TextFirstLastName>
                </EmailContainer>
            ),
            status: <TableBadge status={contact.Status} />,
            dateUnsubscribed: <ContactDateAndTime dateTime={contact.StatusChangeDate} />,
            unsubscribemethod: <TableTextWrapper>{methodTranslation}</TableTextWrapper>,
            unsubscribereason: <TableTextWrapper>{reasonTranslation}</TableTextWrapper>,
            feedback: <TableTextWrapper>{contact.Feedback}</TableTextWrapper>,
            rowID: `unsubscribe-table-${index}`,
        };
    });
};
